import { useState } from 'react';
import { Box, Chip, Tab, Table, TableBody, TableHead, TableRow, Tabs } from '@mui/material';
import { Download } from '@mui/icons-material';
import {
  CustomButton,
  CustomTextField,
  GeneralTableBox,
  HeadTableCell,
  HorizontalForm,
  NoDataSkeleton,
  PaginationTable,
  RowTableCell,
  TableBodySkeleton,
} from '../../../common/components';
import { downloadSupplierLedgerExport } from '../../../hooks/useSupplierLedger';
import {
  COST_AREA_OPTIONS,
  COST_BEHAVIOR_OPTIONS,
  COST_TRACEABILITY_OPTIONS,
} from '../constants/supplierLedgerConstants';
import { compactFormSx, tabPanelSx } from '../constants/supplierLedgerUiConstants';
import {
  getClassificationFieldLabel,
  isClassificationField,
} from '../utils/supplierLedgerHelpers';
import { formatAmount } from '../utils/supplierLedgerFormatters';
import useClientPagination from '../hooks/useClientPagination';
import useSupplierReportsData from '../hooks/useSupplierReportsData';
import { FieldSelect } from './SupplierLedgerShared';

const ReportsTab = () => {
  const [activeReportTab, setActiveReportTab] = useState(0);
  const [filters, setFilters] = useState({
    provider: '',
    concept: '',
    cost_behavior: '',
    cost_traceability: '',
    cost_area: '',
    start_date: '',
    end_date: '',
  });
  const {
    providerRows,
    conceptRows,
    classificationRows,
    providerLoading,
    conceptLoading,
    classificationLoading,
  } = useSupplierReportsData(filters);
  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };
  const reportTables = [
    {
      label: 'Por proveedor',
      rows: providerRows,
      loading: providerLoading,
      columns: [
        ['provider_name', 'Proveedor'],
        ['total_invoiced_ars', 'Facturado'],
        ['paid_amount_ars', 'Pagado'],
        ['ledger_balance_ars', 'Saldo'],
      ],
    },
    {
      label: 'Por concepto',
      rows: conceptRows,
      loading: conceptLoading,
      columns: [
        ['concept', 'Concepto'],
        ['invoice_count', 'Facturas'],
        ['provider_count', 'Proveedores'],
        ['total_amount_ars', 'Total'],
      ],
    },
    {
      label: 'Por clasificación',
      rows: classificationRows,
      loading: classificationLoading,
      columns: [
        ['cost_behavior', 'Fijo/Variable'],
        ['cost_traceability', 'Directo/Indirecto'],
        ['cost_area', 'Área'],
        ['total_amount_ars', 'Total'],
        ['pending_amount_ars', 'Pendiente'],
      ],
    },
  ];
  const activeReport = reportTables[activeReportTab];

  return (
    <Box sx={tabPanelSx}>
      <HorizontalForm withBorder={false} sx={compactFormSx}>
        <CustomButton variant="outlined" startIcon={<Download />} onClick={() => downloadSupplierLedgerExport('invoices')}>Facturas Excel</CustomButton>
        <CustomButton variant="outlined" startIcon={<Download />} onClick={() => downloadSupplierLedgerExport('providers')}>Proveedores Excel</CustomButton>
        <CustomButton variant="outlined" startIcon={<Download />} onClick={() => downloadSupplierLedgerExport('accounting_entries')}>Asientos Excel</CustomButton>
        <CustomTextField label="Proveedor" name="provider" value={filters.provider} onChange={handleFilterChange} sx={{ width: 220 }} />
        <CustomTextField label="Concepto" name="concept" value={filters.concept} onChange={handleFilterChange} sx={{ width: 220 }} />
        <FieldSelect label="Fijo/Variable" name="cost_behavior" value={filters.cost_behavior} onChange={handleFilterChange} options={[{ value: '', label: 'Todos' }, ...COST_BEHAVIOR_OPTIONS]} sx={{ width: 180 }} />
        <FieldSelect label="Directo/Indirecto" name="cost_traceability" value={filters.cost_traceability} onChange={handleFilterChange} options={[{ value: '', label: 'Todos' }, ...COST_TRACEABILITY_OPTIONS]} sx={{ width: 190 }} />
        <FieldSelect label="Área" name="cost_area" value={filters.cost_area} onChange={handleFilterChange} options={[{ value: '', label: 'Todas' }, ...COST_AREA_OPTIONS]} sx={{ width: 180 }} />
        <CustomTextField label="Desde" type="date" name="start_date" value={filters.start_date} onChange={handleFilterChange} shrinkLabel sx={{ width: 170 }} />
        <CustomTextField label="Hasta" type="date" name="end_date" value={filters.end_date} onChange={handleFilterChange} shrinkLabel sx={{ width: 170 }} />
      </HorizontalForm>
      <Box sx={{ backgroundColor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={activeReportTab}
          onChange={(_, value) => setActiveReportTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 44,
            '& .MuiTab-root': {
              minHeight: 44,
              fontSize: 13,
              fontWeight: 600,
            },
          }}
        >
          {reportTables.map((report) => (
            <Tab key={report.label} label={report.label} />
          ))}
        </Tabs>
      </Box>
      <ReportTable rows={activeReport.rows} columns={activeReport.columns} loading={activeReport.loading} />
    </Box>
  );
};

const getReportCellAlign = (field) => {
  if (field.includes('amount') || field.includes('balance') || field.includes('total')) return 'right';
  if (field === 'provider_name' || field === 'concept') return 'left';
  return 'center';
};

const ReportTable = ({ rows, columns, loading = false }) => {
  const reportPagination = useClientPagination(rows);

  return (
    <Box sx={{ backgroundColor: 'background.paper' }}>
      <GeneralTableBox hasMaxHeight={false}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map(([field, label]) => {
                const align = getReportCellAlign(field);
                return (
                  <HeadTableCell key={label} align={align} sx={{ textAlign: align }}>
                    {label}
                  </HeadTableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableBodySkeleton rows={10} />
            ) : rows.length ? reportPagination.paginatedRows.map((row, index) => (
              <TableRow key={index} hover>
                {columns.map(([field]) => {
                  const isAmount = field.includes('amount') || field.includes('balance') || field.includes('total');
                  const align = getReportCellAlign(field);
                  return (
                    <RowTableCell
                      key={field}
                      align={align}
                      sx={{ textAlign: align }}
                    >
                      {isAmount ? formatAmount(row[field]) : (
                        isClassificationField(field) ? (
                          <Chip
                            size="small"
                            label={getClassificationFieldLabel(field, row[field])}
                            sx={{
                              color: 'primary.main',
                              backgroundColor: 'background.formTables',
                              fontWeight: 700,
                            }}
                          />
                        ) : row[field] || '-'
                      )}
                    </RowTableCell>
                  );
                })}
              </TableRow>
            )) : (
              <NoDataSkeleton colSpan={columns.length} message="No hay datos para mostrar." showSubtitle={false} />
            )}
          </TableBody>
        </Table>
      </GeneralTableBox>
      {!!rows.length && (
        <PaginationTable
          page={reportPagination.page}
          totalPages={reportPagination.totalPages}
          totalCount={reportPagination.totalCount}
          rowsPerPage={reportPagination.rowsPerPage}
          onPageChange={reportPagination.handlePageChange}
          onRowsPerPageChange={reportPagination.handleRowsPerPageChange}
          sx={{ mb: 0 }}
        />
      )}
    </Box>
  );
};

export default ReportsTab;
